/**
 * LPRSearchView — Búsqueda de patentes en historial de detecciones
 * Filtros: texto de patente, sitio, rango de fecha, confianza mínima
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuthStore } from '../store/authStore'

const PAGE_SIZE = 50

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTs(ts) {
  if (!ts) return '—'
  const d = new Date(ts)
  return d.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'medium' })
}

function confBadge(conf) {
  const pct = Math.round((conf || 0) * 100)
  const color = pct >= 90 ? '#059669' : pct >= 70 ? '#D97706' : '#DC2626'
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 10,
      background: color + '22', color, minWidth: 36, display: 'inline-block',
      textAlign: 'center',
    }}>
      {pct}%
    </span>
  )
}

// ─── SiteSelect ───────────────────────────────────────────────────────────────

function SiteSelect({ value, onChange, token }) {
  const [sites, setSites] = useState([])

  useEffect(() => {
    fetch('/api/sites/', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setSites)
      .catch(() => {})
  }, [token])

  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        padding: '6px 10px', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border)', background: 'var(--bg-root)',
        color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer',
        minWidth: 160,
      }}
    >
      <option value="">Todos los sitios</option>
      {sites.map(s => (
        <option key={s.id} value={s.id}>{s.name}</option>
      ))}
    </select>
  )
}

// ─── PlateHistory modal ───────────────────────────────────────────────────────

function PlateHistoryModal({ plate, token, onClose }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const params = new URLSearchParams({ plate, limit: 200 })
    fetch(`/api/lpr/search?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => { setItems(d.items || []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [plate, token])

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div
        style={{
          background: 'var(--bg-surface)', borderRadius: 12,
          border: '1px solid var(--border)', width: 640,
          maxHeight: '80vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 18 }}>🚗</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
              {plate}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
              Historial completo de detecciones
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none',
              fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)',
            }}
          >×</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 12 }}>
              Cargando…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)', fontSize: 12 }}>
              Sin detecciones adicionales
            </div>
          )}
          {items.map((item, i) => (
            <div key={item.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '8px 0', borderBottom: '1px solid var(--border)',
              fontSize: 12,
            }}>
              <span style={{ color: 'var(--text-muted)', minWidth: 24, textAlign: 'right' }}>
                {i + 1}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{item.site_name}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                  {item.camera_name || item.camera_id || '—'}
                  {item.zone ? ` · ${item.zone}` : ''}
                </div>
              </div>
              {confBadge(item.confidence)}
              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                {formatTs(item.event_ts)}
              </span>
            </div>
          ))}
        </div>
        <div style={{
          padding: '10px 20px', borderTop: '1px solid var(--border)',
          fontSize: 11, color: 'var(--text-muted)',
        }}>
          {items.length} detección{items.length !== 1 ? 'es' : ''} encontradas
        </div>
      </div>
    </div>
  )
}

// ─── LPRSearchView ────────────────────────────────────────────────────────────

export default function LPRSearchView() {
  const token = useAuthStore(s => s.token)

  const [plate,    setPlate]    = useState('')
  const [siteId,   setSiteId]   = useState('')
  const [fromTs,   setFromTs]   = useState('')
  const [toTs,     setToTs]     = useState('')
  const [minConf,  setMinConf]  = useState(0)
  const [results,  setResults]  = useState(null)
  const [loading,  setLoading]  = useState(false)
  const [offset,   setOffset]   = useState(0)
  const [modal,    setModal]    = useState(null)  // plate string

  const searchRef = useRef(null)

  const search = useCallback(async (off = 0) => {
    setLoading(true)
    setOffset(off)
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: off })
      if (plate.trim())  params.set('plate', plate.trim())
      if (siteId)        params.set('site_id', siteId)
      if (fromTs)        params.set('from_ts', fromTs)
      if (toTs)          params.set('to_ts', toTs)
      if (minConf > 0)   params.set('min_conf', minConf / 100)

      const r = await fetch(`/api/lpr/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!r.ok) throw new Error(`Error ${r.status}`)
      const data = await r.json()
      setResults(data)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [plate, siteId, fromTs, toTs, minConf, token])

  const onKeyDown = (e) => {
    if (e.key === 'Enter') search(0)
  }

  const clearFilters = () => {
    setPlate('')
    setSiteId('')
    setFromTs('')
    setToTs('')
    setMinConf(0)
    setResults(null)
  }

  const totalPages = results ? Math.ceil(results.total / PAGE_SIZE) : 0
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* ── Barra de búsqueda ─────────────────────────────────────────────── */}
      <div style={{
        padding: '16px 20px', background: 'var(--bg-surface)',
        borderBottom: '1px solid var(--border)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>

          {/* Patente */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              Patente
            </label>
            <input
              ref={searchRef}
              value={plate}
              onChange={e => setPlate(e.target.value.toUpperCase())}
              onKeyDown={onKeyDown}
              placeholder="ABCD12 o fragmento…"
              style={{
                padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'var(--bg-root)',
                color: 'var(--text-primary)', fontSize: 13, width: 160,
                fontFamily: 'monospace', letterSpacing: '0.05em',
              }}
            />
          </div>

          {/* Sitio */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              Sitio
            </label>
            <SiteSelect value={siteId} onChange={setSiteId} token={token} />
          </div>

          {/* Desde */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              Desde
            </label>
            <input
              type="datetime-local"
              value={fromTs}
              onChange={e => setFromTs(e.target.value)}
              style={{
                padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'var(--bg-root)',
                color: 'var(--text-primary)', fontSize: 12,
              }}
            />
          </div>

          {/* Hasta */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              Hasta
            </label>
            <input
              type="datetime-local"
              value={toTs}
              onChange={e => setToTs(e.target.value)}
              style={{
                padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)', background: 'var(--bg-root)',
                color: 'var(--text-primary)', fontSize: 12,
              }}
            />
          </div>

          {/* Confianza mínima */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
              Confianza mín. {minConf > 0 ? `(${minConf}%)` : ''}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="range" min={0} max={99} step={5}
                value={minConf}
                onChange={e => setMinConf(Number(e.target.value))}
                style={{ width: 100 }}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 28 }}>
                {minConf > 0 ? `${minConf}%` : 'off'}
              </span>
            </div>
          </div>

          {/* Botones */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginLeft: 'auto' }}>
            {results && (
              <button
                onClick={clearFilters}
                style={{
                  padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
                }}
              >
                Limpiar
              </button>
            )}
            <button
              onClick={() => search(0)}
              disabled={loading}
              style={{
                padding: '6px 18px', borderRadius: 'var(--radius-sm)',
                border: 'none', background: 'var(--accent)', color: '#fff',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Buscando…' : '🔍 Buscar'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Resultados ────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto' }}>

        {/* Estado inicial */}
        {!results && !loading && (
          <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            height: '100%', gap: 12, color: 'var(--text-muted)',
          }}>
            <div style={{ fontSize: 48 }}>🔍</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Búsqueda de patentes LPR</div>
            <div style={{ fontSize: 12, textAlign: 'center', lineHeight: 1.6, maxWidth: 340 }}>
              Ingresa una patente (completa o fragmento) o usa los filtros<br />
              para buscar en el historial de detecciones.
            </div>
          </div>
        )}

        {/* Sin resultados */}
        {results && results.total === 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            height: '100%', gap: 12, color: 'var(--text-muted)',
          }}>
            <div style={{ fontSize: 48 }}>🚫</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Sin resultados</div>
            <div style={{ fontSize: 12 }}>
              No se encontraron detecciones con los filtros actuales.
            </div>
          </div>
        )}

        {/* Tabla */}
        {results && results.total > 0 && (
          <div>
            {/* Header tabla */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr 180px 60px 160px 100px',
              gap: 0,
              padding: '8px 20px',
              background: 'var(--bg-muted)',
              borderBottom: '1px solid var(--border)',
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
              color: 'var(--text-muted)', letterSpacing: '0.05em',
              position: 'sticky', top: 0, zIndex: 1,
            }}>
              <span>Patente</span>
              <span>Sitio / Cámara</span>
              <span>Zona</span>
              <span>Conf.</span>
              <span>Fecha y hora</span>
              <span></span>
            </div>

            {/* Filas */}
            {results.items.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '140px 1fr 180px 60px 160px 100px',
                  gap: 0,
                  padding: '10px 20px',
                  borderBottom: '1px solid var(--border)',
                  alignItems: 'center',
                  fontSize: 12,
                  transition: 'background 0.1s',
                  cursor: 'default',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
                onMouseLeave={e => e.currentTarget.style.background = ''}
              >
                {/* Patente */}
                <span style={{
                  fontFamily: 'monospace', fontWeight: 700, fontSize: 14,
                  letterSpacing: '0.08em', color: 'var(--text-primary)',
                  background: 'var(--bg-muted)', padding: '3px 8px',
                  borderRadius: 4, display: 'inline-block', border: '1px solid var(--border)',
                }}>
                  {item.plate || '—'}
                </span>

                {/* Sitio / Cámara */}
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    {item.site_name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {item.camera_name || item.camera_id || '—'}
                  </div>
                </div>

                {/* Zona */}
                <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
                  {item.zone || '—'}
                </span>

                {/* Confianza */}
                {confBadge(item.confidence)}

                {/* Fecha */}
                <span style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
                  {formatTs(item.event_ts)}
                </span>

                {/* Acción */}
                <button
                  onClick={() => setModal(item.plate)}
                  title="Ver historial de esta patente"
                  style={{
                    padding: '4px 10px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)', background: 'transparent',
                    color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Historial
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Footer / Paginación ───────────────────────────────────────────── */}
      {results && results.total > 0 && (
        <div style={{
          padding: '10px 20px', background: 'var(--bg-surface)',
          borderTop: '1px solid var(--border)', flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 12, fontSize: 12,
        }}>
          <span style={{ color: 'var(--text-muted)' }}>
            {results.total.toLocaleString()} resultado{results.total !== 1 ? 's' : ''}
          </span>
          {totalPages > 1 && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
              <button
                disabled={currentPage <= 1}
                onClick={() => search(offset - PAGE_SIZE)}
                style={{
                  padding: '4px 10px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: currentPage <= 1 ? 'var(--text-muted)' : 'var(--text-primary)',
                  cursor: currentPage <= 1 ? 'not-allowed' : 'pointer', fontSize: 12,
                }}
              >← Anterior</button>
              <span style={{ padding: '0 8px', color: 'var(--text-muted)' }}>
                Pág {currentPage} / {totalPages}
              </span>
              <button
                disabled={currentPage >= totalPages}
                onClick={() => search(offset + PAGE_SIZE)}
                style={{
                  padding: '4px 10px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: currentPage >= totalPages ? 'var(--text-muted)' : 'var(--text-primary)',
                  cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer', fontSize: 12,
                }}
              >Siguiente →</button>
            </div>
          )}
        </div>
      )}

      {/* Modal historial */}
      {modal && (
        <PlateHistoryModal
          plate={modal}
          token={token}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}
